import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, fmtError, BACKEND_URL } from "@/lib/api";
import { fmtDate, fmtEUR, L } from "@/constants/labels";
import { CheckCircle2, XCircle, ShieldOff, ShieldCheck, FileText, ExternalLink } from "lucide-react";
import AdminAssistant from "@/pages/AdminAssistant";
import AdminCatalog from "@/pages/AdminCatalog";
import AdminNotifications from "@/pages/AdminNotifications";
import AdminAvis from "@/pages/AdminAvis";
import AdminVersements from "@/pages/AdminVersements";
import AdminPurge from "@/pages/AdminPurge";

const TYPE_STRUCTURE_LABELS = {
  entreprise_generale: "Entreprise générale du bâtiment",
  syndic: "Syndic de copropriété",
  gestionnaire_patrimoine: "Gestionnaire de patrimoine",
  agence_immo: "Agence immobilière",
  expert_assurance: "Expert en assurance",
  assureur_courtier: "Compagnie ou courtier en assurance",
  maitre_oeuvre: "Maître d'œuvre",
  autre: "Autre",
};

export default function AdminPanel() {
  const [tab, setTab] = useState("stats");
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [docs, setDocs] = useState([]);
  const [missions, setMissions] = useState({ missions: [], total_ht: 0, total_commission: 0 });
  const [parrainages, setParrainages] = useState([]);

  const load = async () => {
    const [s, u, d, m, p] = await Promise.all([
      api.get("/admin/stats"), api.get("/admin/users"),
      api.get("/admin/documents"), api.get("/admin/missions"),
      api.get("/admin/parrainages").catch(() => ({ data: [] })),
    ]);
    setStats(s.data); setUsers(u.data); setDocs(d.data); setMissions(m.data); setParrainages(p.data);
  };
  useEffect(() => { load(); }, []);

  const validate = async (id, statut) => {
    // Chantier 2 (2026-09) : le back attend "verifie" (canonique) et un motif
    // obligatoire pour un refus. On garde ici l'entrée rapide (prompt) pour
    // le legacy — la vraie file de vérification avec motifs prédéfinis est
    // sur la page dédiée /app/admin/documents.
    if (statut === "verifie") {
      try { await api.post(`/admin/documents/${id}/validate`, { statut: "verifie", validation_method: "admin_manual" });
            toast.success("Statut mis à jour"); load(); }
      catch (err) { toast.error(fmtError(err)); }
      return;
    }
    const motif = (prompt("Motif du refus ? (obligatoire — l'artisan recevra cet e-mail)") || "").trim();
    if (motif.length < 5) { toast.error("Motif trop court (5 caractères minimum)"); return; }
    try { await api.post(`/admin/documents/${id}/validate`, { statut: "refuse", motif, validation_method: "admin_manual" });
          toast.success("Refus notifié à l'artisan"); load(); }
    catch (err) { toast.error(fmtError(err)); }
  };
  const suspend = async (userId, suspended) => {
    try { await api.post(`/admin/users/${userId}/suspend`, null, { params: { suspended } }); toast.success("OK"); load(); }
    catch (err) { toast.error(fmtError(err)); }
  };
  const annulerParrainage = async (id) => {
    const motif = prompt("Motif de l'annulation ?");
    if (!motif || motif.length < 3) return;
    try { await api.post(`/admin/parrainages/${id}/annuler`, { motif }); toast.success("Crédit annulé"); load(); }
    catch (err) { toast.error(fmtError(err)); }
  };

  const Tab = ({ id, label }) => (
    <button onClick={() => setTab(id)} data-testid={`admin-tab-${id}`}
      className={`px-4 py-2 text-sm font-semibold border-b-2 ${tab === id ? "border-[#1B3A5C] text-[#1B3A5C]" : "border-transparent text-[#3E4C59]"}`}>
      {label}
    </button>
  );

  return (
    <div>
      <h1 className="text-2xl md:text-3xl font-semibold text-[#1F2933]">Administration</h1>
      <div className="mt-4 border-b border-[#E4E7EB] flex flex-wrap">
        <Tab id="stats" label="Tableau de bord" />
        <Tab id="users" label="Utilisateurs" />
        <Tab id="docs" label="Tous les documents" />
        <Tab id="missions" label="Missions" />
        <Tab id="parrainages" label="Parrainages" />
        <Tab id="notifications" label="Notifications" />
        <Tab id="avis" label="Avis" />
        <Tab id="versements" label="Versements" />
        <Tab id="assistant" label="Assistant" />
        <Tab id="materiel" label="Matériel" />
        <Tab id="vehicules" label="Véhicules" />
        <Tab id="purge" label="Purge" />
      </div>

      {tab === "stats" && stats && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
            {Object.entries({
              "Utilisateurs": stats.users, "Entreprises": stats.entreprises, "Prestataires": stats.prestataires,
              "Besoins ouverts": stats.besoins_ouverts, "Missions": stats.missions, "Docs à contrôler": stats.docs_a_controler,
            }).map(([k, v]) => (
              <div key={k} className="card p-4"><div className="text-xs uppercase text-[#3E4C59]">{k}</div><div className="text-3xl font-bold text-[#1B3A5C]">{v}</div></div>
            ))}
          </div>
          <div className="mt-4">
            <Link to="/app/admin/documents" className="btn-primary inline-flex items-center gap-2" data-testid="admin-goto-docs-queue">
              <ExternalLink size={14} /> Ouvrir la file de vérification des documents
            </Link>
          </div>
        </>
      )}

      {tab === "users" && (
        <div className="card mt-4 overflow-x-auto">
          <table className="table-compact w-full">
            <thead><tr><th>Raison sociale</th><th>Rôle</th><th>Structure</th><th>E-mail</th><th>SIRET</th><th>Créé</th><th>Statut</th><th></th></tr></thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id}>
                  <td className="font-semibold">{u.raison_sociale}</td>
                  <td>{u.role}</td>
                  <td className="text-xs">{TYPE_STRUCTURE_LABELS[u.type_structure] || (u.role === "entreprise" ? "—" : "")}</td>
                  <td>{u.email}</td><td>{u.siret}</td>
                  <td>{fmtDate(u.created_at)}</td>
                  <td>{u.suspended ? <span className="badge-danger">Suspendu</span> : <span className="badge-valid">Actif</span>}</td>
                  <td>
                    {u.suspended
                      ? <button onClick={() => suspend(u.id, false)} className="btn-outline" data-testid={`user-reactivate-${u.id}`}><ShieldCheck size={14} /> Réactiver</button>
                      : <button onClick={() => suspend(u.id, true)} className="btn-danger" data-testid={`user-suspend-${u.id}`}><ShieldOff size={14} /> Suspendre</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "docs" && (
        <div className="card mt-4 overflow-x-auto">
          <table className="table-compact w-full">
            <thead><tr><th>Utilisateur</th><th>Type</th><th>Dépôt</th><th>Expiration</th><th>Statut</th><th>Fichier</th><th></th></tr></thead>
            <tbody>
              {docs.length === 0 && <tr><td colSpan={7} className="text-center text-[#3E4C59]">Aucun document</td></tr>}
              {docs.map(d => (
                <tr key={d.id}>
                  <td className="font-semibold">{d.user_nom}</td>
                  <td>{L.docTypes[d.type] || d.type}</td>
                  <td>{fmtDate(d.date_depot)}</td>
                  <td>{fmtDate(d.date_expiration)}</td>
                  <td>{L.compliance[d.statut] || d.statut}</td>
                  <td>{d.signed_url && <a href={`${BACKEND_URL}${d.signed_url}`} target="_blank" rel="noreferrer" className="btn-outline"><FileText size={14} /> Voir</a>}</td>
                  <td className="flex gap-1">
                    <button onClick={() => validate(d.id, "verifie")} className="btn-primary" data-testid={`doc-valide-${d.id}`}><CheckCircle2 size={14} /></button>
                    <button onClick={() => validate(d.id, "refuse")} className="btn-danger" data-testid={`doc-refuse-${d.id}`}><XCircle size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "missions" && (
        <div className="mt-4">
          <div className="grid md:grid-cols-2 gap-3">
            <div className="card p-4"><div className="text-xs uppercase text-[#3E4C59]">Total HT</div><div className="text-3xl font-bold text-[#1B3A5C]">{fmtEUR(missions.total_ht)}</div></div>
            <div className="card p-4"><div className="text-xs uppercase text-[#3E4C59]">Total commissions (interne)</div><div className="text-3xl font-bold text-[#E8710A]">{fmtEUR(missions.total_commission)}</div></div>
          </div>
          <div className="card mt-4 overflow-x-auto">
            <table className="table-compact w-full">
              <thead><tr><th>Titre</th><th>Entreprise</th><th>Prestataire</th><th>Montant HT</th><th>Commission</th><th>Statut</th></tr></thead>
              <tbody>
                {missions.missions.map(m => (
                  <tr key={m.id}>
                    <td className="font-semibold">{m.titre}</td>
                    <td>{m.entreprise_id}</td><td>{m.prestataire_id}</td>
                    <td>{fmtEUR(m.montant_ht)}</td>
                    <td>{fmtEUR(m.commission_montant)}</td>
                    <td>{L.missionStatus[m.statut] || m.statut}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "parrainages" && (
        <div className="card mt-4 overflow-x-auto">
          <table className="table-compact w-full">
            <thead><tr><th>Parrain</th><th>Code</th><th>Filleul (email)</th><th>SIREN filleul</th><th>Statut</th><th>Crédit</th><th>Date</th><th></th></tr></thead>
            <tbody>
              {parrainages.length === 0 && <tr><td colSpan={8} className="text-center text-[#3E4C59]">Aucun parrainage</td></tr>}
              {parrainages.map(p => (
                <tr key={p.id} data-testid={`admin-parrainage-row-${p.id}`}>
                  <td>{p.parrain_raison_sociale || p.parrain_email}</td>
                  <td className="font-mono text-xs">{p.parrain_code}</td>
                  <td>{p.filleul_email}</td>
                  <td className="font-mono text-xs">{p.filleul_siren || "—"}</td>
                  <td>{p.statut}</td>
                  <td>
                    {p.annulle_par_admin ? <span className="badge-danger" title={p.motif_annulation || ""}>Annulé</span>
                      : p.mois_offert_credite_parrain ? <span className="badge-valid">Crédité</span>
                      : <span className="text-[#3E4C59] text-xs">En attente</span>}
                  </td>
                  <td>{fmtDate(p.created_at)}</td>
                  <td>
                    {!p.annulle_par_admin && (
                      <button onClick={() => annulerParrainage(p.id)} className="btn-outline text-xs" data-testid={`admin-annuler-${p.id}`}>Annuler le crédit</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "notifications" && <AdminNotifications />}
      {tab === "avis" && <AdminAvis />}
      {tab === "versements" && <AdminVersements />}
      {tab === "assistant" && <AdminAssistant />}
      {tab === "materiel" && <AdminCatalog kind="equipment" />}
      {tab === "vehicules" && <AdminCatalog kind="vehicles" />}
      {tab === "purge" && <AdminPurge />}

    </div>
  );
}
